/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'
import { Quill } from 'react-quill'
import { BlockEmbed } from './TableEmbed'

interface TableEditControlsProps {
  quillRef: React.RefObject<any>
}

const getTopLevelRows = (table: HTMLTableElement): HTMLTableRowElement[] =>
  Array.from(
    table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tfoot > tr, :scope > tr')
  ) as HTMLTableRowElement[]

const TableEditControls: React.FC<TableEditControlsProps> = ({ quillRef }) => {
  const [editingTable, setEditingTable] = useState<HTMLElement | null>(null)
  const [, setPosTick] = useState(0)

  // Sync editingTable with whichever .ql-table-edit-mode element lives inside
  // THIS editor's root — other TextEditor instances on the page are ignored.
  useEffect(() => {
    const sync = () => {
      const root = quillRef.current?.getEditor()?.root as HTMLElement | undefined
      if (!root) {
        setEditingTable(null)
        return
      }
      const el = root.querySelector('.ql-table-embed.ql-table-edit-mode') as HTMLElement | null
      setEditingTable(el)
    }
    window.addEventListener('ql-table-edit-change', sync)
    sync()
    return () => window.removeEventListener('ql-table-edit-change', sync)
  }, [quillRef])

  // Reposition strips while scrolling/resizing
  useEffect(() => {
    if (!editingTable) return
    const bump = () => setPosTick((t) => t + 1)
    window.addEventListener('scroll', bump, true)
    window.addEventListener('resize', bump)
    return () => {
      window.removeEventListener('scroll', bump, true)
      window.removeEventListener('resize', bump)
    }
  }, [editingTable])

  // Exit edit mode when clicking outside any table/strip (scoped to this editor)
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest?.('.ql-table-embed') || target?.closest?.('.ql-table-strip-btn')) return
      const root = quillRef.current?.getEditor()?.root as HTMLElement | undefined
      if (!root) return
      const active = root.querySelectorAll('.ql-table-embed.ql-table-edit-mode')
      if (active.length) {
        active.forEach((el) => el.classList.remove('ql-table-edit-mode'))
        setEditingTable(null)
        window.dispatchEvent(new CustomEvent('ql-table-edit-change'))
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [quillRef])

  // Replace a TableEmbed via a single atomic delta (retain → delete → insert).
  // deleteAt is temporarily restored to BlockEmbed's implementation so Quill can
  // actually delete the blot — the override is put back in `finally`.
  const replaceTableEmbed = (wrapper: HTMLElement, newTableHtml: string) => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const root = quill.root as HTMLElement
    if (!root.contains(wrapper)) return
    const blot = (Quill as any).find?.(wrapper)
    if (!blot || blot.domNode !== wrapper) return
    const wasEditMode = wrapper.classList.contains('ql-table-edit-mode')
    const index = quill.getIndex(blot)
    const blotAny = blot as any
    const originalDeleteAt = blotAny.deleteAt
    blotAny.deleteAt = (BlockEmbed as any).prototype.deleteAt
    try {
      const Delta = Quill.import('delta') as any
      quill.updateContents(
        new Delta()
          .retain(index)
          .delete(1)
          .insert({ tableEmbed: newTableHtml }),
        'user'
      )
    } finally {
      blotAny.deleteAt = originalDeleteAt
    }
    if (wasEditMode) {
      const [newBlot] = (quill.getLine(index) || []) as any[]
      const newWrapper = newBlot?.domNode as HTMLElement | undefined
      if (newWrapper?.classList?.contains('ql-table-embed')) {
        newWrapper.classList.add('ql-table-edit-mode')
        setEditingTable(newWrapper)
      }
    }
  }

  const insertRowAt = (rowIndex: number) => {
    if (!editingTable) return
    const table = editingTable.querySelector('table') as HTMLTableElement | null
    if (!table) return
    const tableClone = table.cloneNode(true) as HTMLTableElement
    const rows = getTopLevelRows(tableClone)
    const colCount = rows[0]?.cells.length || 1
    const newRow = document.createElement('tr')
    for (let i = 0; i < colCount; i++) {
      const td = document.createElement('td')
      td.innerHTML = '<br>'
      newRow.appendChild(td)
    }
    if (rowIndex >= rows.length) {
      rows[rows.length - 1].parentElement?.appendChild(newRow)
    } else {
      const refRow = rows[rowIndex]
      refRow.parentElement?.insertBefore(newRow, refRow)
    }
    replaceTableEmbed(editingTable, tableClone.outerHTML)
  }

  const deleteRowAt = (rowIndex: number) => {
    if (!editingTable) return
    const table = editingTable.querySelector('table') as HTMLTableElement | null
    if (!table) return
    const tableClone = table.cloneNode(true) as HTMLTableElement
    const rows = getTopLevelRows(tableClone)
    if (rows.length <= 1) return
    rows[rowIndex]?.remove()
    replaceTableEmbed(editingTable, tableClone.outerHTML)
  }

  const insertColumnAt = (colIndex: number) => {
    if (!editingTable) return
    const table = editingTable.querySelector('table') as HTMLTableElement | null
    if (!table) return
    const tableClone = table.cloneNode(true) as HTMLTableElement
    const rows = getTopLevelRows(tableClone)
    rows.forEach((tr) => {
      const refCell = tr.cells[Math.min(colIndex, tr.cells.length - 1)]
      const isHeader = refCell?.tagName === 'TH'
      const newCell = document.createElement(isHeader ? 'th' : 'td')
      newCell.innerHTML = isHeader ? 'Heading' : '<br>'
      if (colIndex >= tr.cells.length) tr.appendChild(newCell)
      else tr.insertBefore(newCell, tr.cells[colIndex])
    })
    replaceTableEmbed(editingTable, tableClone.outerHTML)
  }

  const deleteColumnAt = (colIndex: number) => {
    if (!editingTable) return
    const table = editingTable.querySelector('table') as HTMLTableElement | null
    if (!table) return
    const firstRow = table.querySelector('tr') as HTMLTableRowElement | null
    if (!firstRow || firstRow.cells.length <= 1) return
    const tableClone = table.cloneNode(true) as HTMLTableElement
    const rows = getTopLevelRows(tableClone)
    rows.forEach((tr) => tr.cells[colIndex]?.remove())
    replaceTableEmbed(editingTable, tableClone.outerHTML)
  }

  const editingTableEl = editingTable && document.contains(editingTable)
    ? (editingTable.querySelector('table') as HTMLTableElement | null)
    : null
  const editingRows = editingTableEl ? getTopLevelRows(editingTableEl) : []
  const tableRect = editingTableEl?.getBoundingClientRect() || null

  if (!tableRect || !editingTableEl) return null

  const colBoundaries: number[] = []
  const colCenters: { x: number }[] = []
  if (editingRows[0]) {
    const cells = Array.from(editingRows[0].cells) as HTMLTableCellElement[]
    cells.forEach((c, i) => {
      const r = c.getBoundingClientRect()
      if (i === 0) colBoundaries.push(r.left)
      colBoundaries.push(r.right)
      colCenters.push({ x: r.left + r.width / 2 })
    })
  }
  const rowBoundaries: number[] = []
  const rowCenters: { y: number }[] = []
  editingRows.forEach((r, i) => {
    const rr = r.getBoundingClientRect()
    if (i === 0) rowBoundaries.push(rr.top)
    rowBoundaries.push(rr.bottom)
    rowCenters.push({ y: rr.top + rr.height / 2 })
  })
  const canDeleteRows = editingRows.length > 1
  const canDeleteCols = !!editingRows[0] && editingRows[0].cells.length > 1

  return (
    <>
      {colBoundaries.map((x, i) => (
        <button
          key={`col-ins-${i}`}
          type='button'
          className='ql-table-strip-btn ql-table-strip-btn--insert'
          style={{ top: tableRect.top - 8, left: x - 6 }}
          title='Insert column here'
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => insertColumnAt(i)}
        >+</button>
      ))}
      {canDeleteCols && colCenters.map((c, i) => (
        <button
          key={`col-del-${i}`}
          type='button'
          className='ql-table-strip-btn ql-table-strip-btn--delete'
          style={{ top: tableRect.top - 10, left: c.x - 6 }}
          title='Delete this column'
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => deleteColumnAt(i)}
        >−</button>
      ))}
      {rowBoundaries.map((y, i) => (
        <button
          key={`row-ins-${i}`}
          type='button'
          className='ql-table-strip-btn ql-table-strip-btn--insert'
          style={{ top: y - 6, left: tableRect.left - 8 }}
          title='Insert row here'
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => insertRowAt(i)}
        >+</button>
      ))}
      {canDeleteRows && rowCenters.map((r, i) => (
        <button
          key={`row-del-${i}`}
          type='button'
          className='ql-table-strip-btn ql-table-strip-btn--delete'
          style={{ top: r.y - 6, left: tableRect.left - 10 }}
          title='Delete this row'
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => deleteRowAt(i)}
        >−</button>
      ))}
    </>
  )
}

export default TableEditControls
